import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Section from "../layout/Section";
import Statement from "../ui/Statement";
import MicroLabel from "../ui/MicroLabel";
import TicketCard from "../ui/TicketCard";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./OwnProduct.module.css";

/*
 * OwnProduct — Zeta, the product Appinfigo funds, builds and runs itself.
 *
 * Laid out as the reference's "champion" block (ref s11): a viewport-tall paper
 * section, a big mixed sans / serif lockup on the left with the ways to get the
 * app under it, and the launch creative large, unframed, on the right.
 *
 * WHY THIS SECTION EXISTS
 * Every agency claims delivery expertise. Appinfigo actually carries the cost
 * and the on-call rota for a quick-commerce platform of its own. That is the
 * most credible thing on the site — but only while it is stated accurately.
 *
 * *** HONESTY RULES — READ BEFORE EDITING ***
 * Zeta is LIVE ON GOOGLE PLAY. It is NOT on the App Store yet. Therefore:
 *   - A store badge renders ONLY for a store whose URL is filled in
 *     SITE_CONTENT.ownProduct. An empty URL renders no badge, never a dead one.
 *     The QR code encodes the same Play listing, so it follows the same rule.
 *   - The headline says "LIVE" only while ownProduct.status === "live" AND the
 *     Play URL is present; otherwise it says "COMING" and the lead falls back to
 *     ownProduct.summary, because promo.sub says "Download now".
 *   - Stores still to come (ownProduct.comingTo) render as PLAIN TEXT under a
 *     "Coming to" label, below the badge — never as links or badges. "App
 *     Store" stays there only while ownProduct.appStoreUrl is empty.
 *   - The Google Play badge is Google's official artwork, self-hosted so the
 *     page makes no third-party request. Per Google's badge guidelines it must
 *     be used unmodified and must link to the Play listing.
 *   - There are no download counts, ratings, user numbers, revenue figures or
 *     reviews anywhere in this file. Do not copy them from the store listing:
 *     they change daily and would be stale the moment this deploys.
 *   - Every string rendered below comes from SITE_CONTENT.ownProduct, apart
 *     from the short UI labels in the LABELS block, which name the block that
 *     follows (or state the status flag) rather than asserting anything new
 *     about the product.
 */

/* TODO(content): the product copy itself lives in
   src/constants/siteContent.js -> SITE_CONTENT.ownProduct. Edit it there; this
   file only presents it. */
const PRODUCT = SITE_CONTENT.ownProduct || {};
const PROMO = PRODUCT.promo || {};

/* TODO(content): UI labels only. Keep the eyebrow to 1-3 words. The headline is
   assembled as "<name> <headlineVerb> <headlineLive | headlineComing>." and the
   last word is the serif accent. */
const LABELS = {
  eyebrow: "Our own app",
  headlineVerb: "is",
  headlineLive: "live",
  headlineComing: "coming",
  apps: "The apps",
  platforms: "Coming to",
  seeApp: "See the app"
};

/* TODO(content): intrinsic pixel size of promo.hero.file. Used ONLY to reserve
   the right box before the file loads (no layout shift) and to keep the creative
   inside one viewport — a stale value never distorts the image, because the
   browser switches to the file's real ratio once it has loaded. */
const HERO_SIZE = { width: 1122, height: 1402 };

const TITLE_ID = "zeta-title";
const APPS_LABEL_ID = "zeta-apps-label";

/* Defensive reads: a missing or mistyped field renders nothing rather than an
   empty shell or an invented placeholder. */
function text(value) {
  return typeof value === "string" ? value.trim() : "";
}

function strings(value) {
  return (Array.isArray(value) ? value : []).map(text).filter(Boolean);
}

const NAME = text(PRODUCT.name);
const PLAY_URL = text(PRODUCT.playStoreUrl);
const APP_STORE_URL = text(PRODUCT.appStoreUrl);
const IS_LIVE = PRODUCT.status === "live" && Boolean(PLAY_URL);

/* promo.sub is download copy ("Download now…"), so it is only honest beside a
   working store badge. Without one the neutral product summary is used. */
const LEAD = (IS_LIVE && text(PROMO.sub)) || text(PRODUCT.summary);

/* A store that has a URL is no longer "coming": drop it from the plain-text list
   even if the content editor forgot to remove it from comingTo. */
const COMING_TO = strings(PRODUCT.comingTo).filter(
  (platform) => !(APP_STORE_URL && /app\s*store/i.test(platform))
);

const APPS = (Array.isArray(PRODUCT.apps) ? PRODUCT.apps : []).filter(
  (app) => app && text(app.name)
);

const PLAY_BADGE = `${process.env.PUBLIC_URL}/images/products/google-play-badge.png`;
const PLAY_BADGE_ALT = `Get ${NAME} on Google Play`;
const QR_SRC = text(PROMO.qr) ? `${process.env.PUBLIC_URL}${text(PROMO.qr)}` : "";
const QR_ALT = `QR code linking to ${NAME} on Google Play`;
const QR_CAPTION = text(PROMO.qrCaption);

const HERO = PROMO.hero && text(PROMO.hero.file) ? PROMO.hero : null;
const HERO_SRC = HERO
  ? `${process.env.PUBLIC_URL}/images/zeta/${text(HERO.file)}`
  : "";
const HERO_ALT = (HERO && text(HERO.alt)) || `${NAME} app`;
const SHOT_STYLE = {
  "--shot-ratio": (HERO_SIZE.width / HERO_SIZE.height).toFixed(4)
};

/* Scroll drift on the creative: it travels a little slower than the page
   (DRIFT_RATE 0.06 -> 0.94x), capped so it can never leave the section's own
   padding. Desktop only — below 981px the creative sits in the text flow. */
const DRIFT_RATE = 0.06;
const DRIFT_MAX_PX = 28;
const DESKTOP_QUERY = "(min-width: 981px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function watchQuery(query, handler) {
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", handler);
    return () => query.removeEventListener("change", handler);
  }
  // Safari < 14 only has the legacy listener API.
  query.addListener(handler);
  return () => query.removeListener(handler);
}

/*
 * Writes --zeta-shift on `targetRef` from the scroll position of `frameRef` (an
 * UNTRANSFORMED ancestor, so the measurement never feeds back into itself).
 * One rAF per scroll burst, no React state. It only listens while the frame is
 * near the viewport, the tab is visible, the viewport is desktop-wide and the
 * user has not asked for reduced motion; the CSS applies the variable under the
 * same two media conditions, so either guard alone is enough.
 */
function useScrollDrift(frameRef, targetRef, enabled) {
  useEffect(() => {
    const frame = frameRef.current;
    const target = targetRef.current;
    if (
      !enabled ||
      !frame ||
      !target ||
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const reducedQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);

    let frameId = 0;
    let running = false;
    let inView = typeof IntersectionObserver === "undefined";

    const paint = () => {
      frameId = 0;
      if (!running) return;
      const rect = frame.getBoundingClientRect();
      const viewport =
        window.innerHeight || document.documentElement.clientHeight || 0;
      const offset = rect.top + rect.height / 2 - viewport / 2;
      const shift = Math.max(
        -DRIFT_MAX_PX,
        Math.min(DRIFT_MAX_PX, offset * -DRIFT_RATE)
      );
      target.style.setProperty("--zeta-shift", `${shift.toFixed(1)}px`);
    };

    const schedule = () => {
      if (running && !frameId) {
        frameId = window.requestAnimationFrame(paint);
      }
    };

    const start = () => {
      if (running) return;
      running = true;
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule);
      schedule();
    };

    const stop = () => {
      running = false;
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      }
    };

    const sync = () => {
      const allowed = desktopQuery.matches && !reducedQuery.matches;
      if (!allowed) {
        target.style.removeProperty("--zeta-shift");
      }
      if (allowed && inView && !document.hidden) {
        start();
      } else {
        stop();
      }
    };

    let observer = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
          });
          sync();
        },
        { rootMargin: "25% 0px 25% 0px" }
      );
      observer.observe(frame);
    }

    const unwatchReduced = watchQuery(reducedQuery, sync);
    const unwatchDesktop = watchQuery(desktopQuery, sync);
    document.addEventListener("visibilitychange", sync);
    sync();

    return () => {
      stop();
      if (observer) observer.disconnect();
      unwatchReduced();
      unwatchDesktop();
      document.removeEventListener("visibilitychange", sync);
      target.style.removeProperty("--zeta-shift");
    };
  }, [frameRef, targetRef, enabled]);
}

export default function OwnProduct() {
  const mediaRef = useRef(null);
  const shotRef = useRef(null);
  /* A creative or QR file that is missing from public/ must never leave a
     broken frame: it simply does not render (the same rule as /zeta). */
  const [shotBroken, setShotBroken] = useState(false);
  const [qrBroken, setQrBroken] = useState(false);

  const showShot = Boolean(HERO_SRC) && !shotBroken;
  const showQr = Boolean(QR_SRC) && !qrBroken;

  useScrollDrift(mediaRef, shotRef, showShot);

  if (!NAME) return null;

  return (
    <Section tone="paper" size="screen" id="zeta" aria-labelledby={TITLE_ID}>
      <div
        className={
          showShot ? styles.layout : `${styles.layout} ${styles.layoutSolo}`
        }
      >
        <div className={styles.copy}>
          <div className={styles.head}>
            <Reveal className={styles.kicker}>
              <MicroLabel as="p" className={styles.eyebrow}>
                <Icon name="mobile" size={16} className={styles.eyebrowIcon} />
                <span>{LABELS.eyebrow}</span>
              </MicroLabel>
            </Reveal>

            {/* An h2: HeadingWipe (App.js) owns its motion — never wrap it in
                Reveal. Two block lines, so the serif word gets a line of its
                own like the reference lockup. */}
            <Statement as="h2" size="h2" id={TITLE_ID}>
              <span className={styles.line}>
                {NAME} {LABELS.headlineVerb}
              </span>{" "}
              <span className={styles.line}>
                <em>{IS_LIVE ? LABELS.headlineLive : LABELS.headlineComing}</em>.
              </span>
            </Statement>

            {LEAD ? (
              <Reveal as="p" className={styles.lead} delay={60}>
                {LEAD}
              </Reveal>
            ) : null}
          </div>

          {/* Badge and QR sit together: the badge is for someone already on a
              phone, the QR for someone reading this on a desktop. Both point at
              the Play listing, so neither renders without its URL. The stores
              still to come sit under the badge as plain text — never a link. */}
          {IS_LIVE || COMING_TO.length ? (
            <Reveal className={styles.get} delay={120}>
              <div className={styles.stores}>
                {IS_LIVE ? (
                  <a
                    className={styles.badge}
                    href={PLAY_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-analytics-interaction="app_store_click:google_play"
                  >
                    {/* Official badge: 646x250 including Google's required
                        clear space, so it is sized by height, never cropped. */}
                    <img
                      src={PLAY_BADGE}
                      alt={PLAY_BADGE_ALT}
                      width="646"
                      height="250"
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                ) : null}

                {COMING_TO.length ? (
                  <MicroLabel as="p" className={styles.comingTo}>
                    <span className={styles.comingLabel}>{LABELS.platforms}</span>{" "}
                    <span className={styles.platforms}>
                      {COMING_TO.map((platform, index) => (
                        <React.Fragment key={platform}>
                          {index > 0 ? (
                            <span className={styles.sep} aria-hidden="true">
                              &middot;
                            </span>
                          ) : null}
                          <span>{platform}</span>
                        </React.Fragment>
                      ))}
                    </span>
                  </MicroLabel>
                ) : null}
              </div>

              {IS_LIVE && showQr ? (
                <TicketCard tone="paper" className={styles.qrCard}>
                  <img
                    className={styles.qr}
                    src={QR_SRC}
                    alt={QR_ALT}
                    width="512"
                    height="512"
                    loading="lazy"
                    decoding="async"
                    onError={() => setQrBroken(true)}
                  />
                  {QR_CAPTION ? (
                    <MicroLabel as="p" className={styles.qrCaption}>
                      {QR_CAPTION}
                    </MicroLabel>
                  ) : null}
                </TicketCard>
              ) : null}
            </Reveal>
          ) : null}

          {/* The four apps, names only — the detail lives on /zeta. */}
          {APPS.length ? (
            <Reveal className={styles.apps} delay={180}>
              <MicroLabel as="p" id={APPS_LABEL_ID} className={styles.appsLabel}>
                {LABELS.apps}
              </MicroLabel>
              {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- list-style: none makes Safari/VoiceOver drop list semantics; the role restores the item count */}
              <ul role="list" className={styles.appList} aria-labelledby={APPS_LABEL_ID}>
                {APPS.map((app) => (
                  <li key={app.name} className={styles.app}>
                    <Icon name={app.icon} size={16} className={styles.appIcon} />
                    <MicroLabel>{app.name}</MicroLabel>
                  </li>
                ))}
              </ul>
            </Reveal>
          ) : null}

          {/* The full product page. Wrapped, not revealed itself: .link-arrow
              sets its own transition, which would replace Reveal's. */}
          <Reveal className={styles.more} delay={180}>
            <Link
              className="link-arrow"
              to="/zeta"
              aria-label={`${LABELS.seeApp}: ${NAME}`}
            >
              {LABELS.seeApp}
            </Link>
          </Reveal>
        </div>

        {showShot ? (
          <div ref={mediaRef} className={styles.media} style={SHOT_STYLE}>
            <Reveal className={styles.frame} delay={120}>
              <img
                ref={shotRef}
                className={styles.shot}
                src={HERO_SRC}
                alt={HERO_ALT}
                width={HERO_SIZE.width}
                height={HERO_SIZE.height}
                loading="lazy"
                decoding="async"
                onError={() => setShotBroken(true)}
              />
            </Reveal>
          </div>
        ) : null}
      </div>
    </Section>
  );
}
